import React, { useEffect } from "react";
import { add, differenceInMonths, isAfter } from "date-fns";
import cn from "classnames";
import _ from "lodash";

import { MAX_DATES } from "config";
import { withConfigContext } from "hooks/useConfig";
import { getDefaultStation } from "lib/utils";
import { getSeasonSearchURL } from "lib/searchUrl";
import t from 'lib/i18n';
import Button, { ButtonTypes } from "components/ui/Button";
import NewDatesSelector from "components/datetime/DatesSelector";
import Passengers from "components/Passengers";
import Promo from "components/Promo";
import StationsSelector from "components/StationsSelector";
import BookingFees from "components/BookingFees";
import useLayout from "hooks/useLayout";
import SeasonTicketOptions from "components/season/SeasonTicketOptions";

import { setLocalStorageItem } from "lib/localStorage";
import "styles/pages/tickets.scss";
import "styles/pages/season-tickets.scss";
import useGTM, { useGTMChange } from "hooks/useGTM";
import * as tracking from "lib/tracking";
import { TICKET_TYPES } from "consts";
import { useFetchSeasonRailcards } from "../../hooks/useFetchSeasonRailcards";
import {withCustomerData} from "../../hooks/useAuth";

const MINIMUM_SEASON_LENGTH_IN_MONTHS = 1;

const SeasonTickets = ({
   onGetValues,
   searchFormsController,
   customerData,
   apiAccessToken,
   apiUrl,
   redirectUrl,
   searchCallBack,
   from,
   to,
   disableBackground,
   mode,
   utmCampaign,
   utmContent,
   utmMedium,
   utmSource,
   brand,
   enablePromoDiscount,
   env,
}) => {

  const {
    form,
    errors,
    handleSubmit,
    getValues,
    register,
    setValue,
    watch,
    localStorageKey
  } = searchFormsController.season;

  const { isMobile } = useLayout();

  const { hasCustomerRailcard1617Saver } = useFetchSeasonRailcards({
    apiUrl,
    brand,
    env,
    customerData
  });

  const dispatchGTM = useGTM();
  const onSubmit = data => {
    const urlSearch = getSeasonSearchURL({
      data,
      redirectUrl,
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
      searchFormsController,
    });
    dispatchGTM(tracking.search());

    setLocalStorageItem(localStorageKey, data);

    if (searchCallBack) {
      searchCallBack(urlSearch, data);
    } else {
      window.top.location.href = urlSearch;
    }
  };

  const {
    fromDateTime,
    hasRailcard,
    hasJCPRailcard,
    isAnnual,
    isCustomLength,
    isMonthly,
    isWeekly,
    isFlexi,
    passengerType,
    toDateTime,
    promoCode,
  } = watch([
    "fromDateTime",
    "hasRailcard",
    "hasJCPRailcard",
    "isAnnual",
    "isCustomLength",
    "isMonthly",
    "isWeekly",
    "isFlexi",
    "passengerType",
    "stationFrom",
    "stationTo",
    "toDateTime",
    "via",
    "viaAvoid",
    "viaAvoidType",
    "promoCode"
  ]);

  useGTMChange(passengerType, tracking.trackSeasonPassenger);
  useGTMChange(hasRailcard, tracking.trackSeasonRailcard);
  useGTMChange(hasJCPRailcard, tracking.trackJCPRailcard);
  useGTMChange(isWeekly, tracking.trackSeasonWeekly);
  useGTMChange(isFlexi, tracking.trackSeasonFlexi);
  useGTMChange(isMonthly, tracking.trackSeasonMonthly);
  useGTMChange(isAnnual, tracking.trackSeasonAnnual);
  useGTMChange(isCustomLength, tracking.trackSeasonCustom);
  useGTMChange(fromDateTime, tracking.trackSeasonStartDate);
  const computedLength = React.useMemo(() => {
    if (isCustomLength) {
      return fromDateTime.getDuration(toDateTime);
    }
    return null;
  }, [fromDateTime, toDateTime, isCustomLength]);
  useGTMChange(computedLength, tracking.trackSeasonCustomLength);

  useEffect(() => {
    !form && setDefaultStations();

    register({ name: "fromDateTime" }, { required: true });
    register({ name: "hasRailcard" }, { required: false });
    register({ name: "hasJCPRailcard" }, { required: false });
    register({ name: "isAnnual" }, { required: false });
    register({ name: "isCustomLength" }, { required: false });
    register({ name: "isMonthly" }, { required: false });
    register({ name: "isWeekly" }, { required: false });
    register({ name: "isFlexi" }, { required: false });
    register({ name: "passengerType" }, { required: false });
    register(
      { name: "stationFrom" },
      {
        required: true,
        validate: {
          sameOrigin: ({ name }) => name !== getValues('stationTo')?.name
        }
      }
    );
    register(
      { name: "stationTo" },
      {
        required: true,
        validate: {
          sameOrigin: ({ name }) => name !== getValues('stationFrom')?.name
        }
      }
    );
    register({ name: "promoCode" }, { required: false });
    register(
      { name: "toDateTime" },
      {
        required: false,
        validate: {
          seasonNotEarlier: value => {
            const isCustomLength = getValues("isCustomLength");

            return (
              !isCustomLength ||
              isAfter(getValues("toDateTime").toDate(), getValues("fromDateTime").toDate())
            );
          },
          seasonMinDuration: value => {
            const isCustomLength = getValues("isCustomLength");

            return (
              !isCustomLength ||
              differenceInMonths(
                getValues("toDateTime").toDate(),
                getValues("fromDateTime").toDate()
              ) >= MINIMUM_SEASON_LENGTH_IN_MONTHS
            );
          }
        }
      }
    );

    onGetValues(getValues);
    searchFormsController.shareStationsBetweenForms();
  }, []);

  useEffect(() => {
    if (!getValues('hasRailcard')) {
      setValue('hasRailcard', hasCustomerRailcard1617Saver);
    }
  }, [hasCustomerRailcard1617Saver]);

  async function setDefaultStations () {
    if (from) {
      const station = await getDefaultStation(from, apiUrl, apiAccessToken);
      setValue('stationFrom', station, { shouldDirty: false });
    }

    if (to) {
      const station = await getDefaultStation(to, apiUrl, apiAccessToken);
      setValue('stationTo', station, { shouldDirty: false });
    }
  }

  const onChangeFromDateTime = newValue => {
    setValue("fromDateTime", fromDateTime?.produce(newValue), true);
  };

  const onChangeToDateTime = newValue => {
    setValue("toDateTime", toDateTime?.produce(newValue), true);
  };

  const setFieldValue = field => value => {
    if (field === "passengerType" && value === "child") {
      setValue("hasRailcard", false);
      setValue("hasJCPRailcard", false);
    }

    if (field === "isCustomLength" && value && !toDateTime) {
      setValue("toDateTime", fromDateTime.addMonths(1));
    }

    setValue(field, value, true);
  };

  const isSubmitButtonDisabled = !(getValues('isAnnual') || getValues('isCustomLength') ||
      getValues('isMonthly') || getValues('isWeekly') || getValues('isFlexi'));

  return (
    <div
      className={cn({
        "otrl-jp__widget__content": !disableBackground
      })}
    >
      <div
        data-test-id='jp__tab--season'
        className={cn({
          "otrl-jp__widget__stations": !disableBackground
        })}
      >
        {/* <pre>debug: {JSON.stringify(getValues(), null, 2)}</pre> */}
        {/* <pre>errors: {JSON.stringify(errors, null, 2)}</pre> */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {!isMobile && (
            <SeasonTicketOptions
              setFieldValue={setFieldValue}
              isWeekly={isWeekly}
              isFlexi={isFlexi}
              isMonthly={isMonthly}
              isAnnual={isAnnual}
              isCustomLength={isCustomLength}
            />
          )}
          <div className="otrl-jp__tickets__control-bar">
            <div className="otrl-jp__tickets__control-bar__stations">
              <StationsSelector
                errors={errors}
                getValues={getValues}
                fromName="seasonStationFrom"
                toName="seasonStationTo"
                setValue={searchFormsController.onStationChange}
                watch={watch}
                ticketType={TICKET_TYPES.SEASON}
              />
            </div>
            {isMobile ? (
              <div className="otrl-jp__season-tickets__journey-type-and-dates">
                <SeasonTicketOptions
                  setFieldValue={setFieldValue}
                  isWeekly={isWeekly}
                  isFlexi={isFlexi}
                  isMonthly={isMonthly}
                  isAnnual={isAnnual}
                  isCustomLength={isCustomLength}
                  isMobile={isMobile}
                />
                <div className={cn("otrl-jp__divider-line")} />
                <NewDatesSelector
                  errors={errors}
                  fromDateTime={fromDateTime}
                  isCustomLength={isCustomLength}
                  isSeason
                  maxDate={add(new Date(), MAX_DATES.SEASON)}
                  maxEndDate={add(
                    new Date(),
                    isCustomLength ? MAX_DATES.SEASON_CUSTOM_LENGTH : MAX_DATES.SEASON
                  )}
                  onChangeFromDateTime={onChangeFromDateTime}
                  onChangeToDateTime={onChangeToDateTime}
                  toDateTime={toDateTime}
                />
              </div>
            ) : (
              <NewDatesSelector
                errors={errors}
                fromDateTime={fromDateTime}
                isCustomLength={isCustomLength}
                isSeason
                maxDate={add(new Date(), MAX_DATES.SEASON)}
                maxEndDate={add(
                  new Date(),
                  isCustomLength ? MAX_DATES.SEASON_CUSTOM_LENGTH : MAX_DATES.SEASON
                )}
                onChangeFromDateTime={onChangeFromDateTime}
                onChangeToDateTime={onChangeToDateTime}
                toDateTime={toDateTime}
              />
            )}

            <Passengers
              hasRailcard={hasRailcard}
              hasJCPRailcard={hasJCPRailcard}
              isSeason
              onChange={setFieldValue}
              passengerType={passengerType}
            />

            {enablePromoDiscount && <Promo onChange={setFieldValue} promoCode={promoCode} />}

            <Button
              className="otrl-jp__tickets__submit"
              text={t(isMobile ? "tickets.submit.long" : "tickets.submit")}
              type={ButtonTypes.SUBMIT}
              disabled={isSubmitButtonDisabled}
            />
            <BookingFees />
          </div>
        </form>
      </div>
    </div>
  );
};

export default _.flow(withCustomerData, withConfigContext)(SeasonTickets)
